import React from "react";
import { motion } from "framer-motion";
import Navbar from "../../../DashBoardComponents/Navbar";
import {
  FlaskConical,
  FileText,
  Search,
  CalendarCheck,
  ShieldCheck,
  Activity,
  Building2,
  Clock3,
  ArrowRight,
  Check,
  ClipboardList,
  Stethoscope,
  UserRound,
  Network,
  RefreshCcw,
  Lock,
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, ease: "easeOut" },
  },
};

const fadeLeft = {
  hidden: { opacity: 0, x: -40 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.75, ease: "easeOut" },
  },
};

const fadeRight = {
  hidden: { opacity: 0, x: 40 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.75, ease: "easeOut" },
  },
};

const stagger = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.1 },
  },
};

const viewport = {
  once: true,
  amount: 0.18,
};

const diagnosticServices = [
  {
    icon: FlaskConical,
    title: "Lab Tests",
    desc: "Help patients discover and access supported laboratory testing services.",
  },
  {
    icon: Activity,
    title: "Diagnostic Tests",
    desc: "Connect patients with supported diagnostic services as part of their healthcare journey.",
  },
  {
    icon: CalendarCheck,
    title: "Test Scheduling",
    desc: "Make it easier to find available diagnostic services and manage appointments where supported.",
  },
  {
    icon: FileText,
    title: "Diagnostic Reports",
    desc: "Keep relevant diagnostic reports organised and connected to the patient's health information.",
  },
  {
    icon: ClipboardList,
    title: "Results",
    desc: "Help patients access supported diagnostic results when they become available.",
  },
  {
    icon: RefreshCcw,
    title: "Connected Care",
    desc: "Keep relevant diagnostic information connected to consultations and ongoing care.",
  },
];

const howItWorks = [
  {
    icon: Search,
    step: "01",
    title: "Find a Service",
    desc: "Search for the diagnostic or laboratory service that matches the patient's needs.",
  },
  {
    icon: CalendarCheck,
    step: "02",
    title: "Schedule a Test",
    desc: "Choose an available appointment or diagnostic workflow where scheduling is supported.",
  },
  {
    icon: FlaskConical,
    step: "03",
    title: "Complete the Test",
    desc: "Visit the relevant diagnostic provider and complete the requested test or service.",
  },
  {
    icon: FileText,
    step: "04",
    title: "Receive Results",
    desc: "Access supported diagnostic reports or results when they are made available.",
  },
  {
    icon: Stethoscope,
    step: "05",
    title: "Continue Care",
    desc: "Use relevant results as part of follow-up consultations and ongoing healthcare.",
  },
];

const patientBenefits = [
  "Find relevant diagnostic services more easily",
  "Keep diagnostic reports organised",
  "Reduce the need to search across disconnected documents",
  "Connect relevant test results with the broader care journey",
  "Make information easier to access during follow-up consultations",
];

const professionalBenefits = [
  "Access relevant diagnostic information when authorised",
  "Understand available test results alongside the care journey",
  "Reduce manual searching for previous reports",
  "Support better-informed follow-up consultations",
];

const faqs = [
  {
    q: "What are diagnostic services?",
    a: "Diagnostic services are healthcare services used to investigate, measure or evaluate a patient's health. They can include laboratory tests and other supported diagnostic procedures.",
  },
  {
    q: "Can I book diagnostic tests through Doxzon?",
    a: "Where diagnostic booking is supported, Doxzon can help patients find and schedule available diagnostic services. The exact options depend on the services and providers connected to the platform.",
  },
  {
    q: "Can I access my diagnostic reports?",
    a: "Supported diagnostic reports and results can be made available through the relevant Doxzon workflow when they are received from connected services.",
  },
  {
    q: "Can doctors view diagnostic results?",
    a: "Authorised healthcare professionals may access relevant diagnostic information according to the permissions, workflows and integrations implemented by Doxzon.",
  },
  {
    q: "Are diagnostic results automatically connected to my health record?",
    a: "This depends on the diagnostic service and integration available in Doxzon. Where supported, relevant results can be connected to the patient's health information.",
  },
  {
    q: "Does Doxzon diagnose patients?",
    a: "Doxzon provides healthcare technology and connected workflows. Diagnostic interpretation and clinical decisions remain the responsibility of qualified healthcare professionals.",
  },
];

const users = [
  { icon: UserRound, label: "Patients" },
  { icon: Stethoscope, label: "Doctors" },
  { icon: Building2, label: "Clinics" },
  { icon: FlaskConical, label: "Diagnostic Centers" },
  { icon: Network, label: "Healthcare Networks" },
  { icon: ClipboardList, label: "Care Teams" },
];

const Diagnostics = () => {
  return (
    <>
      <Navbar />

      <main className="font-jakarta bg-white text-slate-900 overflow-hidden">

        {/* =====================================================
            HERO
        ====================================================== */}
        <section className="relative min-h-[600px] bg-gradient-to-br from-cyan-50 via-white to-blue-50 overflow-hidden">

          <motion.div
            className="absolute -top-40 -right-40 w-[620px] h-[620px] rounded-full bg-teal-300/20 blur-3xl"
            animate={{
              scale: [1, 1.15, 1],
              x: [0, -25, 0],
              y: [0, 25, 0],
            }}
            transition={{
              duration: 9,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />

          <motion.div
            className="absolute -bottom-48 -left-40 w-[550px] h-[550px] rounded-full bg-cyan-300/20 blur-3xl"
            animate={{ scale: [1, 1.2, 1], x: [0, 30, 0] }}
            transition={{
              duration: 11,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />

          <div
            className="absolute inset-0 opacity-[0.035]"
            style={{
              backgroundImage:
                "linear-gradient(#0f172a 1px, transparent 1px), linear-gradient(90deg, #0f172a 1px, transparent 1px)",
              backgroundSize: "45px 45px",
            }}
          />

          <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-24 grid lg:grid-cols-2 gap-14 items-center">

            <motion.div
              variants={fadeLeft}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
            >
              <div className="inline-flex items-center gap-2 rounded-full border border-teal-200 bg-white/80 backdrop-blur px-3 py-1.5">
                <motion.span
                  className="w-2 h-2 rounded-full bg-teal-500"
                  animate={{
                    scale: [1, 1.5, 1],
                    opacity: [1, 0.5, 1],
                  }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                />

                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-600">
                  Diagnostics
                </span>
              </div>

              <h1 className="font-sora text-4xl md:text-5xl lg:text-[46px] font-medium tracking-[-0.05em] leading-[1.1] mt-6">
                Find the Right
                <span className="relative block text-teal-500 mt-2">
                  Diagnostic Care
                  <motion.span
                    className="absolute left-0 bottom-[-6px] h-[3px] rounded-full bg-cyan-400"
                    initial={{ width: 0 }}
                    animate={{ width: "58%" }}
                    transition={{ delay: 0.8, duration: 0.8 }}
                  />
                </span>
              </h1>

              <p className="text-slate-600 text-base leading-7 mt-6 max-w-xl">
                Discover supported lab and diagnostic services, manage testing
                workflows, and keep relevant reports connected to your
                healthcare journey.
              </p>

              <div className="flex flex-wrap gap-5 mt-7 text-sm text-slate-500">
                <span className="flex items-center gap-2">
                  <ShieldCheck size={16} className="text-teal-500" />
                  Connected healthcare
                </span>

                <span className="flex items-center gap-2">
                  <Check size={16} className="text-teal-500" />
                  Organised reports
                </span>
              </div>
            </motion.div>

            {/* HERO PRODUCT VISUAL */}
            <motion.div
              variants={fadeRight}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              className="relative"
            >
              <motion.div
                className="absolute inset-[-25px] rounded-[32px] border border-teal-200/60"
                animate={{ rotate: 360 }}
                transition={{
                  duration: 25,
                  repeat: Infinity,
                  ease: "linear",
                }}
              />

              <motion.div
                className="absolute inset-[-45px] rounded-[40px] border border-cyan-200/40"
                animate={{ rotate: -360 }}
                transition={{
                  duration: 35,
                  repeat: Infinity,
                  ease: "linear",
                }}
              />

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1, delay: 0.2 }}
                whileHover={{ scale: 1.015 }}
                className="relative z-10 mx-auto max-w-sm rounded-[22px] overflow-hidden border-[6px] border-white shadow-[0_20px_55px_rgba(15,23,42,0.15)] bg-white"
              >
                <div className="p-4 sm:p-5">

                  <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                    <div>
                      <p className="text-[9px] uppercase tracking-widest text-slate-400">
                        Diagnostics
                      </p>
                      <h3 className="text-base font-bold mt-1">
                        Diagnostic Dashboard
                      </h3>
                    </div>

                    <div className="w-8 h-8 rounded-lg bg-teal-50 grid place-items-center">
                      <FlaskConical size={16} className="text-teal-600" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2.5 mt-4">
                    <div className="rounded-lg bg-slate-50 p-3">
                      <p className="text-[9px] text-slate-400 uppercase">
                        Tests
                      </p>
                      <p className="text-base font-bold mt-1">
                        Available
                      </p>
                    </div>

                    <div className="rounded-lg bg-teal-50 p-3">
                      <p className="text-[9px] text-teal-600 uppercase">
                        Reports
                      </p>
                      <p className="text-base font-bold mt-1 text-slate-900">
                        Connected
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2.5 mt-3.5">
                    {[
                      {
                        icon: FlaskConical,
                        title: "Laboratory Test",
                        meta: "Diagnostic service",
                      },
                      {
                        icon: CalendarCheck,
                        title: "Scheduled Test",
                        meta: "Appointment workflow",
                      },
                      {
                        icon: FileText,
                        title: "Diagnostic Report",
                        meta: "Result available",
                      },
                    ].map(({ icon: Icon, title, meta }) => (
                      <motion.div
                        key={title}
                        whileHover={{ x: 4 }}
                        className="flex items-center gap-2.5 p-2.5 rounded-lg bg-slate-50 border border-slate-100"
                      >
                        <div className="w-8 h-8 rounded-lg bg-white grid place-items-center flex-shrink-0">
                          <Icon size={15} className="text-teal-600" />
                        </div>

                        <div className="flex-1">
                          <p className="text-xs font-semibold">
                            {title}
                          </p>
                          <p className="text-[10px] text-slate-400 mt-0.5">
                            {meta}
                          </p>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-100">
                    <Lock size={13} className="text-teal-600" />
                    <p className="text-[9px] text-slate-400">
                      Access is subject to permissions and applicable controls.
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                className="absolute left-0 lg:left-1 top-8 z-20 bg-white rounded-xl shadow-lg border border-slate-100 p-3 w-40"
                animate={{ y: [0, -10, 0] }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center">
                    <Search size={16} className="text-teal-600" />
                  </div>

                  <div>
                    <p className="text-[9px] text-slate-400">
                      Discovery
                    </p>
                    <p className="text-xs font-bold">
                      Find a test
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                className="absolute right-0 lg:right-1 bottom-12 z-20 bg-white rounded-xl shadow-lg border border-slate-100 p-3 w-44"
                animate={{ y: [0, 10, 0] }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-lg bg-cyan-50 flex items-center justify-center">
                    <FileText size={16} className="text-cyan-600" />
                  </div>

                  <div>
                    <p className="text-[9px] text-slate-400">
                      Results
                    </p>
                    <p className="text-xs font-bold">
                      Reports connected
                    </p>
                  </div>
                </div>

                <div className="mt-2.5 h-1 bg-slate-100 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full"
                    initial={{ width: "0%" }}
                    animate={{ width: "100%" }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatDelay: 3,
                    }}
                  />
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* =====================================================
            JOURNEY STRIP
        ====================================================== */}
        <motion.section
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="max-w-6xl mx-auto px-6 -mt-8 relative z-20"
        >
          <div className="bg-white rounded-2xl border border-slate-100 shadow-lg p-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                ["01", "Find"],
                ["02", "Schedule"],
                ["03", "Test"],
                ["04", "Results"],
              ].map(([value, label]) => (
                <div key={label} className="text-center">
                  <p className="font-sora text-2xl font-bold text-slate-950">
                    {value}
                  </p>
                  <p className="text-xs text-slate-500 mt-1.5">
                    {label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* =====================================================
            INTRO
        ====================================================== */}
        <section className="py-20">
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center"
          >
            <motion.div variants={fadeLeft}>
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-500">
                Connected Diagnostics
              </span>

              <h2 className="font-sora text-3xl md:text-4xl font-medium tracking-[-0.04em] leading-tight mt-3">
                From testing
                <span className="text-teal-500"> to care.</span>
              </h2>
            </motion.div>

            <motion.div variants={fadeRight}>
              <p className="text-base text-slate-600 leading-7">
                Diagnostic information can be an important part of a patient's
                healthcare journey. Doxzon is designed to help connect
                supported diagnostic services, reports and care workflows.
              </p>

              <p className="text-base text-slate-600 leading-7 mt-4">
                When relevant results are available, they can help patients and
                authorised healthcare professionals continue the next step of
                care.
              </p>
            </motion.div>
          </motion.div>
        </section>

        {/* =====================================================
            SERVICES
        ====================================================== */}
        <section className="relative bg-[#f6fbfc] py-20 overflow-hidden">
          <motion.div
            className="absolute -right-40 top-20 w-[500px] h-[500px] rounded-full bg-teal-200/20 blur-3xl"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 8, repeat: Infinity }}
          />

          <div className="relative max-w-7xl mx-auto px-6">

            <div className="max-w-2xl">
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-500">
                Diagnostic Services
              </span>

              <h2 className="font-sora text-3xl md:text-4xl font-medium tracking-[-0.04em] mt-3">
                One workflow for
                <span className="text-teal-500"> diagnostic care.</span>
              </h2>
            </div>

            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.15 }}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10"
            >
              {diagnosticServices.map(({ icon: Icon, title, desc }, index) => (
                <motion.div
                  key={title}
                  variants={fadeUp}
                  whileHover={{ y: -6 }}
                  className="group relative bg-white rounded-2xl border border-slate-100 p-5 shadow-sm hover:shadow-xl transition-shadow duration-300"
                >
                  <motion.div
                    whileHover={{ rotate: 8, scale: 1.08 }}
                    className="w-11 h-11 rounded-xl bg-teal-50 flex items-center justify-center group-hover:bg-teal-500 transition-colors"
                  >
                    <Icon
                      size={19}
                      className="text-teal-600 group-hover:text-white transition-colors"
                    />
                  </motion.div>

                  <span className="absolute top-5 right-5 font-mono text-xs text-slate-200 group-hover:text-teal-300">
                    0{index + 1}
                  </span>

                  <h3 className="font-sora text-lg font-semibold mt-5">
                    {title}
                  </h3>

                  <p className="text-slate-500 text-sm leading-6 mt-2">
                    {desc}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* =====================================================
            HOW IT WORKS
        ====================================================== */}
        <section className="py-20">
          <div className="max-w-6xl mx-auto px-6">

            <div className="text-center">
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-500">
                How it works
              </span>

              <h2 className="font-sora text-3xl md:text-4xl font-medium mt-3">
                Five steps.
                <span className="text-teal-500">
                  {" "}Connected diagnostics.
                </span>
              </h2>
            </div>

            <div className="relative grid md:grid-cols-5 gap-8 md:gap-4 mt-14">

              <motion.div className="hidden md:block absolute top-7 left-[10%] right-[10%] h-[2px] bg-slate-100 overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-teal-500 to-cyan-500"
                  initial={{ width: "0%" }}
                  whileInView={{ width: "100%" }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.8 }}
                />
              </motion.div>

              {howItWorks.map(({ icon: Icon, step, title, desc }) => (
                <motion.div
                  key={step}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={viewport}
                  whileHover={{ y: -6 }}
                  className="relative text-center"
                >
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    className="relative z-10 mx-auto w-14 h-14 rounded-xl bg-slate-950 border-4 border-white shadow-lg flex items-center justify-center"
                  >
                    <Icon size={20} className="text-teal-400" />
                  </motion.div>

                  <span className="block text-[11px] font-mono text-teal-500 tracking-widest mt-4">
                    STEP {step}
                  </span>

                  <h3 className="font-sora text-lg font-bold mt-2">
                    {title}
                  </h3>

                  <p className="text-slate-500 text-sm leading-6 mt-2 max-w-sm mx-auto">
                    {desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* =====================================================
            BENEFITS
        ====================================================== */}
        <section className="relative overflow-hidden bg-slate-950 py-20 text-white">
          <div className="relative max-w-6xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-14 items-center">

              <motion.div
                variants={fadeLeft}
                initial="hidden"
                whileInView="visible"
                viewport={viewport}
              >
                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-400">
                  Why Doxzon
                </span>

                <h2 className="font-sora text-3xl md:text-4xl font-medium leading-tight mt-4">
                  Less searching.
                  <br />
                  Better-connected care.
                </h2>

                <p className="text-slate-400 text-base leading-7 mt-5 max-w-lg">
                  Diagnostic information should not become another disconnected
                  part of the healthcare journey. The goal is to make relevant
                  information easier to find, access and use.
                </p>
              </motion.div>

              <motion.div
                variants={stagger}
                initial="hidden"
                whileInView="visible"
                viewport={viewport}
                className="space-y-3"
              >
                {[
                  ...patientBenefits,
                  ...professionalBenefits,
                ].map((benefit) => (
                  <motion.div
                    key={benefit}
                    variants={fadeUp}
                    whileHover={{
                      scale: 1.02,
                      backgroundColor: "rgba(255,255,255,0.08)",
                    }}
                    className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-xl p-4"
                  >
                    <div className="w-6 h-6 rounded-full bg-teal-500/15 flex items-center justify-center flex-shrink-0">
                      <Check size={13} className="text-teal-400" />
                    </div>

                    <span className="text-sm text-slate-300 leading-6">
                      {benefit}
                    </span>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* =====================================================
            FOR HEALTHCARE PROFESSIONALS
        ====================================================== */}
        <section className="bg-slate-50 py-20">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            className="max-w-6xl mx-auto px-6 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center"
          >
            <div>
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-500">
                For Healthcare Professionals
              </span>

              <h2 className="font-sora text-3xl md:text-4xl font-medium tracking-tight mt-3">
                Relevant Results.
                <br />
                Better Context.
              </h2>

              <p className="text-gray-600 mt-5 leading-7">
                When authorised and available, relevant diagnostic information
                can give healthcare professionals useful context during
                consultations and follow-up care.
              </p>
            </div>

            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              className="grid grid-cols-2 gap-3"
            >
              {[
                { icon: FileText, label: "Diagnostic Reports" },
                { icon: Activity, label: "Test Results" },
                { icon: FlaskConical, label: "Lab Information" },
                { icon: Stethoscope, label: "Care Context" },
              ].map(({ icon: Icon, label }) => (
                <motion.div
                  key={label}
                  variants={fadeUp}
                  whileHover={{ y: -6, scale: 1.02 }}
                  className="bg-white border border-slate-100 rounded-xl p-4 text-center shadow-sm hover:shadow-lg transition"
                >
                  <div className="w-9 h-9 mx-auto rounded-lg bg-gradient-to-br from-teal-500 to-cyan-500 grid place-items-center">
                    <Icon size={17} className="text-white" />
                  </div>

                  <p className="text-xs font-semibold text-slate-800 mt-2.5">
                    {label}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </section>

        {/* =====================================================
            FOR PATIENTS
        ====================================================== */}
        <section className="py-20 bg-gradient-to-r from-cyan-50 to-blue-50 relative overflow-hidden">
          <motion.div
            className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-teal-500/10 blur-3xl"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 7, repeat: Infinity }}
          />

          <div className="relative max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">

            <motion.div
              variants={fadeLeft}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
            >
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-500">
                For Patients
              </span>

              <h2 className="font-sora text-3xl md:text-4xl font-medium tracking-tight mt-3">
                Diagnostics
                <br />
                <span className="text-teal-500">Without the Confusion</span>
              </h2>

              <p className="text-gray-600 mt-5 leading-7">
                Find relevant services, understand where your diagnostic
                information belongs in the care journey, and keep supported
                reports easier to access.
              </p>
            </motion.div>

            <motion.ul
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              className="space-y-2.5"
            >
              {patientBenefits.map((item) => (
                <motion.li
                  key={item}
                  variants={fadeUp}
                  whileHover={{ x: 6 }}
                  className="flex items-center gap-3 bg-white/80 backdrop-blur border border-white rounded-lg p-3.5"
                >
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-teal-500/10 text-teal-600 grid place-items-center">
                    <Check size={14} strokeWidth={2.5} />
                  </span>

                  <span className="text-gray-700 text-sm font-medium">
                    {item}
                  </span>
                </motion.li>
              ))}
            </motion.ul>
          </div>
        </section>

        {/* =====================================================
            WHO CAN USE
        ====================================================== */}
        <section className="bg-cyan-50 py-20 relative overflow-hidden">
          <div className="relative max-w-6xl mx-auto px-6 text-center">

            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-500">
              Connected Care Ecosystem
            </span>

            <h2 className="font-sora text-3xl md:text-4xl font-medium tracking-tight mt-3">
              Diagnostics Across the
              <span className="text-teal-500"> Care Ecosystem</span>
            </h2>

            <p className="text-gray-600 mt-5 max-w-3xl mx-auto leading-7 text-base">
              Patients, doctors, clinics and diagnostic services can interact
              with relevant information in different ways depending on their
              role and permissions.
            </p>

            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-12"
            >
              {users.map(({ icon: Icon, label }) => (
                <motion.div
                  key={label}
                  variants={fadeUp}
                  whileHover={{ y: -6, scale: 1.02 }}
                  className="group bg-white rounded-xl p-4 shadow-md border border-transparent hover:border-teal-200 hover:shadow-xl transition"
                >
                  <div className="w-10 h-10 mx-auto rounded-lg bg-gradient-to-br from-teal-500 to-cyan-500 grid place-items-center">
                    <Icon size={18} className="text-white" />
                  </div>

                  <h3 className="font-sora font-semibold text-base text-slate-900 mt-3">
                    {label}
                  </h3>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* =====================================================
            SECURITY
        ====================================================== */}
        <section className="bg-slate-50 py-20">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            className="max-w-4xl mx-auto px-6 text-center"
          >
            <div className="w-12 h-12 mx-auto rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 grid place-items-center shadow-lg">
              <ShieldCheck size={20} className="text-white" />
            </div>

            <span className="inline-block text-teal-500 uppercase tracking-widest font-semibold text-[11px] mt-5">
              Security &amp; Privacy
            </span>

            <h2 className="font-sora text-3xl md:text-4xl font-medium tracking-tight mt-2.5">
              Diagnostic Information Deserves Careful Protection
            </h2>

            <p className="text-gray-600 mt-5 leading-7 text-base">
              Diagnostic information can contain sensitive health data.
              Doxzon is designed to support appropriate access controls,
              permissions and privacy practices for supported healthcare
              workflows.
            </p>
          </motion.div>
        </section>

        {/* =====================================================
            DIAGNOSTICS VS LAB TESTS
        ====================================================== */}
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-6">

            <div className="text-center max-w-3xl mx-auto">
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-500">
                Understanding Diagnostics
              </span>

              <h2 className="font-sora text-3xl md:text-4xl font-medium tracking-tight mt-3">
                Diagnostics is more than
                <span className="text-teal-500"> one lab test</span>
              </h2>

              <p className="text-gray-600 mt-5 leading-7 text-base">
                Laboratory testing is one part of diagnostics. A connected
                diagnostics workflow can also include finding services,
                scheduling, completing tests, receiving reports and continuing
                care.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mt-10">
              {[
                {
                  title: "Lab Tests",
                  icon: FlaskConical,
                  desc: "Tests performed on samples such as blood or other specimens to provide relevant laboratory information.",
                },
                {
                  title: "Diagnostics",
                  icon: Activity,
                  desc: "A broader healthcare workflow that can include diagnostic services, testing, reports and follow-up care.",
                },
              ].map(({ title, icon: Icon, desc }) => (
                <motion.div
                  key={title}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={viewport}
                  whileHover={{ y: -5 }}
                  className="bg-slate-50 border border-slate-100 rounded-2xl p-5"
                >
                  <div className="w-10 h-10 rounded-xl bg-teal-50 grid place-items-center">
                    <Icon size={18} className="text-teal-600" />
                  </div>

                  <h3 className="font-sora text-lg font-semibold mt-4">
                    {title}
                  </h3>

                  <p className="text-slate-600 text-sm leading-6 mt-2">
                    {desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* =====================================================
            FAQ
        ====================================================== */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-6">

            <div className="text-center">
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-500">
                FAQ
              </span>

              <h2 className="font-sora text-3xl md:text-4xl font-medium tracking-tight mt-3">
                Frequently Asked Questions
              </h2>
            </div>

            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              className="mt-10 space-y-3"
            >
              {faqs.map((faq, index) => (
                <motion.div
                  key={faq.q}
                  variants={fadeUp}
                  whileHover={{ y: -4 }}
                  className="bg-white border border-slate-100 rounded-xl p-5 hover:border-teal-200 hover:shadow-lg transition"
                >
                  <div className="flex items-start gap-3.5">
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 text-white grid place-items-center text-xs font-bold">
                      0{index + 1}
                    </span>

                    <div>
                      <h3 className="font-sora text-base md:text-lg font-semibold text-slate-900">
                        {faq.q}
                      </h3>

                      <p className="text-gray-600 text-sm mt-2 leading-6">
                        {faq.a}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* =====================================================
            FINAL CTA
        ====================================================== */}
        <section className="px-6 pb-20">
          <div className="max-w-7xl mx-auto rounded-3xl bg-slate-950 text-white px-8 py-12 md:px-14 text-center">

            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-400">
              Connected Diagnostics
            </span>

            <h2 className="font-sora text-3xl md:text-4xl font-medium tracking-tight mt-3">
              Make diagnostics part of a
              <span className="text-teal-400"> connected care journey.</span>
            </h2>

            <p className="text-slate-300 text-sm max-w-2xl mx-auto mt-4 leading-6">
              Find supported diagnostic services, organise relevant reports,
              and keep important information connected to ongoing care.
            </p>
          </div>
        </section>

      </main>
    </>
  );
};

export default Diagnostics;